/**
 * Areas under a normal curve with any mean and SD, and the inverse: the x that cuts off
 * a given area. Shared by the normal-distribution and sampling-distribution calculators
 * (a sampling distribution is a normal curve whose SD is the standard error).
 */

import {
  normalCdf,
  normalQuantile,
  normalUpper,
} from "@/lib/stats/distributions";

export type NormalRegion = "below" | "above" | "between" | "outside";

export interface RegionResult {
  probability: number;
  /** The standardised bounds, z = (x − μ)/σ. */
  zLow: number;
  zHigh: number | null;
  /** The x-intervals whose area is the probability, for shading a sketch. */
  shaded: [number, number][];
}

export function isValidNormal(mu: number, sigma: number): boolean {
  return Number.isFinite(mu) && Number.isFinite(sigma) && sigma > 0;
}

/**
 * P(a < X < b), taking the difference in whichever tail keeps it precise — both bounds
 * deep in the upper tail would otherwise subtract two numbers that are nearly 1.
 */
function between(a: number, b: number, mu: number, sigma: number): number {
  if (a >= mu) {
    return normalUpper(a, mu, sigma) - normalUpper(b, mu, sigma);
  }
  return normalCdf(b, mu, sigma) - normalCdf(a, mu, sigma);
}

export function normalRegion(
  region: NormalRegion,
  mu: number,
  sigma: number,
  a: number,
  b: number | null
): RegionResult | null {
  if (!isValidNormal(mu, sigma)) {
    return null;
  }
  const inf = Number.POSITIVE_INFINITY;
  const z = (x: number) => (x - mu) / sigma;
  if (region === "below") {
    return {
      probability: normalCdf(a, mu, sigma),
      zLow: z(a),
      zHigh: null,
      shaded: [[-inf, a]],
    };
  }
  if (region === "above") {
    return {
      probability: normalUpper(a, mu, sigma),
      zLow: z(a),
      zHigh: null,
      shaded: [[a, inf]],
    };
  }
  if (b === null) {
    return null;
  }
  const low = Math.min(a, b);
  const high = Math.max(a, b);
  const inside = Math.max(0, between(low, high, mu, sigma));
  if (region === "between") {
    return {
      probability: inside,
      zLow: z(low),
      zHigh: z(high),
      shaded: [[low, high]],
    };
  }
  return {
    probability: normalCdf(low, mu, sigma) + normalUpper(high, mu, sigma),
    zLow: z(low),
    zHigh: z(high),
    shaded: [
      [-inf, low],
      [high, inf],
    ],
  };
}

export interface InverseResult {
  /** P(X < x) = p. */
  left: number;
  /** P(X > x) = p. */
  right: number;
  /** The central interval μ ± z σ holding probability p. */
  centralLow: number;
  centralHigh: number;
  /** z for the left-tail answer and the half-width z of the central interval. */
  zLeft: number;
  zCentral: number;
}

export function normalInverse(
  p: number,
  mu: number,
  sigma: number
): InverseResult | null {
  if (!(isValidNormal(mu, sigma) && p > 0 && p < 1)) {
    return null;
  }
  const zLeft = normalQuantile(p);
  // −Φ⁻¹((1 − p)/2) rather than Φ⁻¹((1 + p)/2): no rounding of 1 + p near p = 1.
  const zCentral = -normalQuantile((1 - p) / 2);
  return {
    left: mu + sigma * zLeft,
    right: mu - sigma * zLeft,
    centralLow: mu - sigma * zCentral,
    centralHigh: mu + sigma * zCentral,
    zLeft,
    zCentral,
  };
}

/** μ ± 4σ, widened to show any bound the reader typed that lies further out. */
export function sketchBounds(
  mu: number,
  sigma: number,
  points: readonly number[]
): [number, number] {
  let low = mu - 4 * sigma;
  let high = mu + 4 * sigma;
  for (const x of points) {
    if (Number.isFinite(x)) {
      low = Math.min(low, x - 0.5 * sigma);
      high = Math.max(high, x + 0.5 * sigma);
    }
  }
  // Never so wide that the curve is a spike: cap at μ ± 8σ.
  return [Math.max(low, mu - 8 * sigma), Math.min(high, mu + 8 * sigma)];
}
